import { API_HOST } from "../config/api";
import { useState, useEffect, useCallback } from "react";
import base64 from "base-64";

const useAuth = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const setToken = (token) => {
    try {
      const payload = JSON.parse(base64.decode(token.split(".")[1]));
      localStorage.setItem("access_token", token);
      setIsLoggedIn(true);
      setCurrentUser({
        id: payload.id,
        exp: payload.exp,
        nickname: payload.nickname,
        subscription_end_date: payload.subscription_end_date,
      });

      return payload;
    } catch (error) {
      console.error("Token processing error:", error);
      return null;
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem("access_token");
    setIsLoggedIn(false);
    setCurrentUser(null);
  }, []);

  const checkTokenValidity = useCallback(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        const payload = JSON.parse(base64.decode(token.split(".")[1]));
        const currentTime = Date.now() / 1000;

        if (payload.exp < currentTime) {
          logout();
          return false;
        } else {
          setIsLoggedIn(true);
          setCurrentUser({
            id: payload.id,
            exp: payload.exp,
            nickname: payload.nickname,
            subscription_end_date: payload.subscription_end_date,
          });
          return true;
        }
      } catch (error) {
        console.error("Token validation error:", error);
        logout();
        return false;
      }
    }
    logout();
    return false;
  }, [logout]);

  const refreshUserInfo = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      return false;
    }

    try {
      const response = await fetch(
        `${API_HOST}/api/v1b/user/me`,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const userData = await response.json();
        // API 응답 구조: { data: { id, nickname, subscription_end_date, ... } }
        const userInfo = userData.data || userData;
        
        // 사용자 정보 업데이트 (기존 정보 유지하면서 업데이트)
        setCurrentUser((prev) => {
          if (!prev) {
            // 이전 정보가 없으면 토큰에서 가져오기
            try {
              const payload = JSON.parse(base64.decode(token.split(".")[1]));
              return {
                id: userInfo.id || payload.id,
                exp: payload.exp,
                nickname: userInfo.nickname || payload.nickname,
                subscription_end_date: userInfo.subscription_end_date || payload.subscription_end_date,
              };
            } catch (error) {
              return {
                id: userInfo.id,
                exp: null,
                nickname: userInfo.nickname,
                subscription_end_date: userInfo.subscription_end_date,
              };
            }
          }
          return {
            ...prev,
            id: userInfo.id || prev.id,
            nickname: userInfo.nickname || prev.nickname,
            subscription_end_date: userInfo.subscription_end_date || prev.subscription_end_date,
          };
        });

        return true;
      } else {
        // 토큰이 만료되었을 수 있음
        if (response.status === 401) {
          logout();
        }
        return false;
      }
    } catch (error) {
      console.error("Failed to refresh user info:", error);
      return false;
    }
  }, []);

  // 토큰을 강제로 재발급 받는 함수 (결제 완료 후 사용)
  const refreshToken = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      return false;
    }

    try {
      // 토큰 재발급 API 호출
      const response = await fetch(
        `${API_HOST}/api/v1b/login/renew_token`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const newToken = data.access_token;
        
        if (newToken) {
          // 새 토큰으로 업데이트
          setToken(newToken);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      return false;
    }
  }, []);

  useEffect(() => {
    checkTokenValidity();

    // 10초마다 토큰 유효성 체크
    const tokenCheckInterval = setInterval(() => {
      checkTokenValidity();
    }, 10000);

    // 다른 탭에서의 로그인/로그아웃 감지
    const handleStorageChange = (e) => {
      if (e.key === "access_token") {
        checkTokenValidity();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      clearInterval(tokenCheckInterval);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [checkTokenValidity]);

  return {
    isLoggedIn,
    currentUser,
    setToken,
    checkTokenValidity,
    refreshUserInfo,
    refreshToken,
    logout,
  };
};

export default useAuth;
